import apiV2 from '../api/v2';
import type { Manufacturer } from '../utils/manufacturer';

export interface PatientGetBase {
    id: string;
    name: string;
    firstName: string;
    lastName: string;
    email: string | null;
    phone: string | null;
    gender: string | null;
    dateOfBirth: string | null;
    profilePictureUrl: string | null;
    profileThumbnailUrl: string | null;
}

export interface PatientStatusBlock {
    caseStatus: string | null;
    actionRequired: string | null;
    internalCaseStatus: string | null;
    orders: string | null;
    version: string | null;
}

export interface PatientTreatmentBlock {
    caseType: string | null;
    subCaseType: string | null;
    /** Raw Monday "Aligner Preference" label. Prefer `manufacturerSlug` for logic. */
    manufacturer: string | null;
    /** Normalized manufacturer identity — gate UI on this, not the raw label. */
    manufacturerSlug: Manufacturer | null;
    manufacturerLink: string | null;
    alignerOption: string | null;
    additionalInfo: string | null;
    caseAcknowledge: string | null;
    suggestedFees: string | null;
    estimatedTreatmentDuration: string | null;
    treatmentDuration: string | null;
    refinementsCount: number | null;
    manufacturerCaseId: string | null;
    patientClassification: string | null;
    caseComplexity: string | null;
    designType: string | null;
    expedite: string | null;
}

export interface PatientMonitoringBlock {
    monitoringStatus: string | null;
    quickstartType: string | null;
    currentAligner: number | null;
    totalAligners: number | null;
    dmAppActivationStatus: string | null;
    dmLinkActivationCount: number | null;
    dmLink: string | null;
    dmId: number | null;
    iteroScanId: string | null;
    dmProfileId: string | null;
    dmScanFrequency: string | null;
    /** UTC day the current Treatment phase began, which VA week triggers count from. */
    treatmentStartDate: string | null;
}

export interface PatientDatesBlock {
    approvalDate: string | null;
    scheduledStartDate: string | null;
    photosDate: string | null;
    nextAppointmentDate: string | null;
    submissionDate: string | null;
    nextAppointmentReason: string | null;
    scansDate: string | null;
    sparkSubmittedDate: string | null;
}

export interface PatientOrthopulseBlock {
    recommendation: string | null;
    status: string | null;
    price: string | null;
}

export interface PatientClinicalBlock {
    cbctUploadLink: string | null;
    dxReportLink: string | null;
    easyRxLink: string | null;
    sdiNotes: string | null;
}

export interface PatientOrthoBlock {
    alignerWear: string | null;
    leftElastics: string | null;
    rightElastics: string | null;
    leftElasticsSize: string | null;
    rightElasticsSize: string | null;
    provideAlignerCount: string | null;
    nextApptAtAligner: string | null;
    treatingDocPreferences: string | null;
    futureNotes: string | null;
    vcaTeamInfo: string | null;
}

export interface PatientNotesBlock {
    treatmentGoals: string | null;
    doctorGoals: string | null;
    dmNotes: string | null;
    refinementNotes: string | null;
    refinementGoal: string | null;
    refinementRejectionReason: string | null;
    retentionNotes: string | null;
    retentionRejectionReason: string | null;
}

export interface PatientPracticeBlock {
    id: string;
    name: string;
    drFirstName: string | null;
    drLastName: string | null;
    sparkAcct: string | null;
    invisalignAcct: string | null;
    accountType: string | null;
    designPreferences: string | null;
    scanner: string | null;
    drEmail: string | null;
    phone: string | null;
    email: string | null;
    city: string | null;
    state: string | null;
    country: string | null;
}

export interface PatientBoardsBlock {
    patientBoardId: string;
    orthoBoardId: string;
    orthoItemId: string | null;
}

export interface GetPatientResponse extends PatientGetBase {
    status?: PatientStatusBlock;
    treatment?: PatientTreatmentBlock;
    monitoring?: PatientMonitoringBlock;
    dates?: PatientDatesBlock;
    orthopulse?: PatientOrthopulseBlock;
    clinical?: PatientClinicalBlock;
    ortho?: PatientOrthoBlock;
    notes?: PatientNotesBlock;
    practice?: PatientPracticeBlock;
    boards?: PatientBoardsBlock;
}

const ALL_BLOCKS =
    'status,treatment,monitoring,dates,orthopulse,clinical,ortho,notes,practice,boards';

export async function fetchPatientCard(patientId: string): Promise<GetPatientResponse> {
    const response = await apiV2.get<{ success: boolean; data: GetPatientResponse }>(
        `/patients/${patientId}?include=${ALL_BLOCKS}`
    );
    return response.data.data;
}
